import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { LEAD_STATUSES, STATUS_STYLES } from "@/lib/leadConstants";
import LeadDialog from "@/components/LeadDialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Plus, Search, LayoutList, KanbanSquare, Phone, Mail } from "lucide-react";

export default function Leads() {
  const navigate = useNavigate();
  const [view, setView] = useState("table");
  const [leads, setLeads] = useState(null);
  const [team, setTeam] = useState([]);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [dialogOpen, setDialogOpen] = useState(false);

  const load = useCallback(() => {
    const params = new URLSearchParams();
    if (search) params.set("search", search);
    if (statusFilter !== "all") params.set("status", statusFilter);
    api.get(`/leads?${params}`).then(({ data }) => setLeads(data));
  }, [search, statusFilter]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { api.get("/business/team").then(({ data }) => setTeam(data)).catch(() => {}); }, []);

  const moveStage = async (lead, status) => {
    setLeads((prev) => prev.map((l) => (l.id === lead.id ? { ...l, status } : l)));
    try { await api.patch(`/leads/${lead.id}/status`, { status }); } catch { load(); }
  };

  const teamName = (id) => team.find((m) => m.id === id)?.name || "—";

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold font-heading">Leads</h1>
          <p className="text-slate-500 mt-1">Manage your sales pipeline.</p>
        </div>
        <div className="flex items-center gap-2">
          <div className="flex gap-1 bg-white border border-slate-200 rounded-lg p-1">
            <button onClick={() => setView("table")} data-testid="view-table" className={`p-1.5 rounded-md ${view === "table" ? "bg-blue-600 text-white" : "text-slate-500"}`}><LayoutList className="h-4 w-4" /></button>
            <button onClick={() => setView("kanban")} data-testid="view-kanban" className={`p-1.5 rounded-md ${view === "kanban" ? "bg-blue-600 text-white" : "text-slate-500"}`}><KanbanSquare className="h-4 w-4" /></button>
          </div>
          <Button onClick={() => setDialogOpen(true)} data-testid="add-lead-btn" className="bg-blue-600 hover:bg-blue-700"><Plus className="h-4 w-4 mr-1" /> New lead</Button>
        </div>
      </div>

      <div className="flex flex-col sm:flex-row gap-3">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
          <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search leads..." data-testid="lead-search" className="pl-9" />
        </div>
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-full sm:w-52" data-testid="lead-status-filter"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All statuses</SelectItem>
            {LEAD_STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      {leads === null ? (
        <Skeleton className="h-96 rounded-xl" />
      ) : leads.length === 0 && statusFilter === "all" && !search ? (
        <Card className="p-12 text-center border-slate-200" data-testid="leads-empty">
          <KanbanSquare className="h-12 w-12 text-slate-300 mx-auto" />
          <h3 className="mt-4 font-heading font-semibold text-lg">No leads yet</h3>
          <p className="text-slate-500 mt-1">Add your first lead or share your booking form to start capturing them.</p>
          <Button onClick={() => setDialogOpen(true)} className="mt-4 bg-blue-600 hover:bg-blue-700"><Plus className="h-4 w-4 mr-1" /> Add a lead</Button>
        </Card>
      ) : view === "table" ? (
        <Card className="border-slate-200 overflow-hidden">
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Name</TableHead>
                  <TableHead className="hidden md:table-cell">Service</TableHead>
                  <TableHead className="hidden lg:table-cell">Source</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="hidden lg:table-cell">Assigned</TableHead>
                  <TableHead className="hidden sm:table-cell text-right">Value</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {leads.map((l) => (
                  <TableRow key={l.id} className="cursor-pointer hover:bg-slate-50" onClick={() => navigate(`/app/leads/${l.id}`)} data-testid={`lead-row-${l.id}`}>
                    <TableCell>
                      <div className="font-medium">{l.name}</div>
                      <div className="text-xs text-slate-500 flex items-center gap-2">
                        {l.phone && <span className="flex items-center gap-1"><Phone className="h-3 w-3" />{l.phone}</span>}
                      </div>
                    </TableCell>
                    <TableCell className="hidden md:table-cell text-slate-600">{l.service || "—"}</TableCell>
                    <TableCell className="hidden lg:table-cell"><Badge variant="secondary">{l.source}</Badge></TableCell>
                    <TableCell><Badge className={`border ${STATUS_STYLES[l.status]}`}>{l.status}</Badge></TableCell>
                    <TableCell className="hidden lg:table-cell text-slate-600">{l.assigned_to ? teamName(l.assigned_to) : "—"}</TableCell>
                    <TableCell className="hidden sm:table-cell text-right font-medium">${Number(l.estimated_value || 0).toLocaleString()}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </Card>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-4" data-testid="kanban-board">
          {LEAD_STATUSES.map((status) => {
            const items = leads.filter((l) => l.status === status);
            return (
              <div key={status} className="bg-slate-100/70 rounded-xl p-3" data-testid={`kanban-col-${status.replace(/ /g, "-")}`}>
                <div className="flex items-center justify-between mb-3 px-1">
                  <span className="text-sm font-semibold text-slate-700">{status}</span>
                  <Badge variant="secondary" className="text-xs">{items.length}</Badge>
                </div>
                <div className="space-y-2 min-h-[60px]">
                  {items.map((l) => (
                    <Card key={l.id} className="p-3 border-slate-200 hover:shadow-md transition-shadow cursor-pointer" onClick={() => navigate(`/app/leads/${l.id}`)} data-testid={`kanban-card-${l.id}`}>
                      <div className="font-medium text-sm">{l.name}</div>
                      <div className="text-xs text-slate-500 mt-0.5">{l.service || "No service"}</div>
                      {l.estimated_value > 0 && <div className="text-xs font-medium text-emerald-600 mt-1">${Number(l.estimated_value).toLocaleString()}</div>}
                      <div onClick={(e) => e.stopPropagation()} className="mt-2">
                        <Select value={l.status} onValueChange={(v) => moveStage(l, v)}>
                          <SelectTrigger className="h-7 text-xs" data-testid={`kanban-move-${l.id}`}><SelectValue /></SelectTrigger>
                          <SelectContent>{LEAD_STATUSES.map((s) => <SelectItem key={s} value={s} className="text-xs">{s}</SelectItem>)}</SelectContent>
                        </Select>
                      </div>
                    </Card>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {dialogOpen && <LeadDialog open={dialogOpen} onOpenChange={setDialogOpen} team={team} onSaved={load} />}
    </div>
  );
}
